import React from 'react';
import {
  Box,
  Table,
  TableRow,
  TableCell,
  tableCellClasses,
  TableContainer,
  TextField,
  Typography
} from '@mui/material';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import GetTableCell from '../../common/GetTableCell';
import {
  testingTabObject,
  auditTestingTabObject,
  SOC2testingTabObject,
  tableBoxStyle,
  tableHeaderStyle
} from '../constants/testingConfigs';
import {
  BackupCheckForm,
  GenericCheckForm,
  PasswordCheckForm,
  PrivilegeCheckForm,
  SensitiveTcodeCheckForm,
  TerminationCheckForm
} from '../../ServiceDeliveryTesting/components';
import { useServiceDeliveryCheck } from '../../ServiceDeliveryTesting/hooks/useServiceDeliveryCheck';
import {
  backupCheckRequest,
  genericCheckRequest,
  passwordCheckRequest,
  privilegeCheckRequest,
  sensitiveTcodeCheckRequest,
  terminationCheckRequest
} from '../../../../constants/requests';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  paddingTop: '0px',
  paddingBottom: '0px',
  paddingRight: '5px',
  paddingLeft: '5px',
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  },
  outline: 'none',
  border: 'none'
}));

const StyledTableRow = styled(TableRow)(() => ({
  paddingTop: '0px',
  paddingBottom: '0px',
  margin: '0px',
  '&:last-child td, &:last-child th': {
    border: 0
  },
  border: '0px solid black',
  outline: 'none'
}));

/**
 * MasterTableSheet Component
 * Renders individual testing sheet for Master Table rows
 * Uses strategy pattern to determine which config to use
 *
 * @param {number} rowIndex - The index of the row
 * @param {Object} rowData - The row data
 * @param {Object} RCMData - The complete RCM data
 * @param {string} type - The template type
 * @param {string} testingSheetType - The testing sheet type
 * @param {Function} handleChangeRCM - Callback for cell changes
 * @param {Object} dynamicTestingTabObject - Dynamic configuration for custom types
 */
const MasterTableSheet = ({
  rowIndex,
  rowData,
  RCMData,
  type,
  testingSheetType,
  handleChangeRCM,
  dynamicTestingTabObject
}) => {
  const {
    handleSubmit: handleTerminationCheckSubmit,
    isLoading: isTerminationCheckLoading
  } = useServiceDeliveryCheck(
    terminationCheckRequest,
    'termination_check_output.xlsx'
  );
  const {
    handleSubmit: handleGenericCheckSubmit,
    isLoading: isGenericCheckLoading
  } = useServiceDeliveryCheck(genericCheckRequest, 'generic_check_output.xlsx');
  const {
    handleSubmit: handlePrivilegeCheckSubmit,
    isLoading: isPrivilegeCheckLoading
  } = useServiceDeliveryCheck(
    privilegeCheckRequest,
    'privilege_check_output.xlsx'
  );
  const {
    handleSubmit: handleSensitiveTcodeCheckSubmit,
    isLoading: isSensitiveTcodeCheckLoading
  } = useServiceDeliveryCheck(
    sensitiveTcodeCheckRequest,
    'sensitive_tcode_check_output.xlsx'
  );
  const {
    handleSubmit: handlePasswordCheckSubmit,
    isLoading: isPasswordCheckLoading
  } = useServiceDeliveryCheck(
    passwordCheckRequest,
    'password_check_output.xlsx'
  );
  const {
    handleSubmit: handleBackupCheckSubmit,
    isLoading: isBackupCheckLoading
  } = useServiceDeliveryCheck(backupCheckRequest, 'backup_check_output.zip');

  const testingType =
    RCMData?.rows?.['Master Table']?.[rowIndex]?.['Testing Type'];

  // Get the appropriate configuration based on type
  const getConfigAndRenderer = () => {
    // Custom/Mazar dynamic configuration
    if (
      testingSheetType === 'BDO' &&
      type !== 'BDO_SAP' &&
      type !== 'BDO_GENERIC' &&
      type !== 'SOC2.0' &&
      type !== 'SOC1.0' &&
      type !== 'ISAE 3402' &&
      type !== 'Template B1' &&
      type !== 'Template B2' &&
      type !== 'BDO-D365' &&
      type !== 'BDO-SAP' &&
      type !== 'BDO-Generic' &&
      type !== 'BDO-Oracle' &&
      type !== 'BDO-Tally'
    ) {
      return { config: dynamicTestingTabObject, renderer: 'dynamic' };
    }

    // BDO types
    if (
      type === 'BDO-D365' ||
      type === 'BDO-SAP' ||
      type === 'BDO-Generic' ||
      type === 'BDO-Oracle' ||
      type === 'BDO-Tally'
    ) {
      return { config: auditTestingTabObject, renderer: 'audit' };
    }

    // Template B1/B2
    if (
      type === 'Template B1' ||
      type === 'Template B2' ||
      (type !== 'SOC2.0' && type !== 'SOC1.0' && type !== 'ISAE 3402')
    ) {
      return { config: testingTabObject, renderer: 'testing' };
    }

    // SOC types
    if (type === 'SOC2.0' || type === 'SOC1.0' || type === 'ISAE 3402') {
      return { config: SOC2testingTabObject, renderer: 'soc2' };
    }

    return { config: testingTabObject, renderer: 'testing' };
  };

  const { config, renderer } = getConfigAndRenderer();

  const renderDynamicSheet = () => {
    return Object.keys(config).map((tableName, index) => (
      <Box key={`${tableName}-${index}`} style={tableBoxStyle}>
        <Box style={tableHeaderStyle}>{tableName}</Box>
        <TableContainer component={Paper}>
          <Table
            sx={{ minWidth: 700 }}
            aria-label="customized table"
            style={{ marginBottom: '10px' }}
          >
            {Object.keys(config[tableName]).map((key) => {
              return (
                <StyledTableRow
                  hover
                  key={`RCM-${tableName}-${rowIndex}-${index}-${key}`}
                >
                  <StyledTableCell
                    style={{ minWidth: '50px', minHeight: '0px' }}
                    align="left"
                  >
                    <TextField
                      fullWidth
                      multiline
                      defaultValue={config[tableName][key].displayName}
                      disabled={true}
                      style={{
                        outline: 'none',
                        marginBottom: '7px',
                        marginTop: '3px'
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell
                    style={{ minWidth: '300px', minHeight: '0px' }}
                    align="left"
                  >
                    <GetTableCell
                      key={`rcm-cell-${rowIndex}-${key}`}
                      rowNumber={rowIndex}
                      fieldName={key}
                      fieldType={
                        RCMData.meta.fields[key]?.fieldType ||
                        'multiLineTextField'
                      }
                      value={RCMData['rows']['Master Table'][rowIndex][key]}
                      onChangeCB={(rowNumber, fieldName, cellValue) =>
                        handleChangeRCM(
                          rowNumber,
                          fieldName,
                          cellValue,
                          'Master Table'
                        )
                      }
                      isEditable={config[tableName][key].isEditable}
                      options={RCMData.meta.fields[key]?.options || {}}
                      styles={{
                        padding: '10px',
                        margin: '5px',
                        borderRadius: '5px'
                      }}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </Table>
        </TableContainer>
      </Box>
    ));
  };

  const renderAuditSheet = () => {
    return Object.keys(config).map((tableName, index) => (
      <Box key={`${tableName}-${index}`} style={tableBoxStyle}>
        <Box style={tableHeaderStyle}>{tableName}</Box>
        <TableContainer component={Paper}>
          <Table
            sx={{ minWidth: 700 }}
            aria-label="customized table"
            style={{ marginBottom: '10px' }}
          >
            {config[tableName].map((key) => {
              return (
                <StyledTableRow
                  hover
                  key={`RCM-${tableName}-${rowIndex}-${index}-${key}`}
                >
                  <StyledTableCell
                    style={{ minWidth: '50px', minHeight: '0px' }}
                    align="left"
                  >
                    <TextField
                      fullWidth
                      multiline
                      defaultValue={
                        RCMData.meta.TestingSheetTab[key]?.displayName || key
                      }
                      disabled={true}
                      style={{
                        outline: 'none',
                        marginBottom: '7px',
                        marginTop: '3px'
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell
                    style={{ minWidth: '300px', minHeight: '0px' }}
                    align="left"
                  >
                    <GetTableCell
                      key={`rcm-cell-${rowIndex}-${key}`}
                      rowNumber={rowIndex}
                      fieldName={key}
                      fieldType={
                        RCMData.meta.fields[key]?.fieldType ||
                        'multiLineTextField'
                      }
                      value={
                        key === 'Result' && tableName === 'Overall Conclusion'
                          ? rowData['Result']
                          : rowData[key]
                      }
                      onChangeCB={(rowNumber, fieldName, cellValue) =>
                        handleChangeRCM(
                          rowNumber,
                          fieldName,
                          cellValue,
                          'Master Table'
                        )
                      }
                      isEditable={
                        tableName === 'Overall Conclusion' ? false : true
                      }
                      options={RCMData.meta.fields[key]?.options}
                      styles={{
                        padding: '10px',
                        margin: '5px',
                        borderRadius: '5px'
                      }}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </Table>
        </TableContainer>
      </Box>
    ));
  };

  const renderTestingSheet = () => {
    return Object.keys(config).map((tableName, index) => (
      <Box key={`${tableName}-${index}`} style={tableBoxStyle}>
        <Box style={tableHeaderStyle}>{tableName}</Box>
        <TableContainer component={Paper}>
          <Table
            sx={{ minWidth: 700 }}
            aria-label="customized table"
            style={{ marginBottom: '10px' }}
          >
            {config[tableName].map((key) => {
              return (
                <StyledTableRow
                  hover
                  key={`RCM-${tableName}-${rowIndex}-${index}-${key}`}
                >
                  <StyledTableCell
                    style={{ minWidth: '50px', minHeight: '0px' }}
                    align="left"
                  >
                    <TextField
                      fullWidth
                      multiline
                      defaultValue={
                        RCMData.meta.TestingSheetTab[key]?.displayName || key
                      }
                      disabled={true}
                      style={{
                        outline: 'none',
                        marginBottom: '7px',
                        marginTop: '3px'
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell
                    style={{ minWidth: '300px', minHeight: '0px' }}
                    align="left"
                  >
                    <GetTableCell
                      key={`rcm-cell-${rowIndex}-${key}`}
                      rowNumber={rowIndex}
                      fieldName={key}
                      fieldType={
                        RCMData.meta.fields[key]?.fieldType ||
                        'multiLineTextField'
                      }
                      value={
                        key === 'Result' && tableName === 'Overall Conclusion'
                          ? rowData['Additional Conclusion'] !== 'N/A'
                            ? rowData['Additional Conclusion']
                            : rowData[key]
                          : tableName === 'Overall Conclusion' &&
                            key !== 'Result'
                          ? rowData[key]?.concat(
                              '\n\n',
                              rowData[`Additional ${key}`]
                            )
                          : rowData[key]
                      }
                      onChangeCB={(rowNumber, fieldName, cellValue) =>
                        handleChangeRCM(
                          rowNumber,
                          fieldName,
                          cellValue,
                          'Master Table'
                        )
                      }
                      isEditable={
                        tableName === 'Overall Conclusion' ? false : true
                      }
                      options={RCMData.meta.fields[key]?.options}
                      styles={{
                        padding: '10px',
                        margin: '5px',
                        borderRadius: '5px'
                      }}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </Table>
        </TableContainer>
      </Box>
    ));
  };

  const renderSOC2Sheet = () => {
    return Object.keys(config).map((tableName, index) => (
      <Box key={`${tableName}-${index}`} style={tableBoxStyle}>
        <Box style={tableHeaderStyle}>{tableName}</Box>
        <TableContainer component={Paper}>
          <Table
            sx={{ minWidth: 700 }}
            aria-label="customized table"
            style={{ marginBottom: '10px' }}
          >
            {config[tableName].map((key) => {
              return (
                <StyledTableRow
                  hover
                  key={`RCM-${tableName}-${rowIndex}-${index}-${key}`}
                >
                  <StyledTableCell
                    style={{ minWidth: '50px', minHeight: '0px' }}
                    align="left"
                  >
                    <TextField
                      fullWidth
                      multiline
                      defaultValue={
                        RCMData.meta.TestingSheetTab[key]?.displayName || key
                      }
                      disabled={true}
                      style={{
                        outline: 'none',
                        marginBottom: '7px',
                        marginTop: '3px'
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell
                    style={{ minWidth: '300px', minHeight: '0px' }}
                    align="left"
                  >
                    <GetTableCell
                      key={`rcm-cell-${rowIndex}-${key}`}
                      rowNumber={rowIndex}
                      fieldName={key}
                      fieldType={
                        RCMData.meta.fields[key]?.fieldType ||
                        'multiLineTextField'
                      }
                      value={
                        tableName === 'Overall Conclusion' &&
                        key === 'Overall Conclusion (Effective / Ineffective)'
                          ? rowData['Operating Effectiveness'] === 'Ineffective'
                            ? 'Ineffective'
                            : rowData['Design  Effectiveness']
                          : rowData[key]
                      }
                      onChangeCB={(rowNumber, fieldName, cellValue) =>
                        handleChangeRCM(
                          rowNumber,
                          fieldName,
                          cellValue,
                          'Master Table'
                        )
                      }
                      isEditable={
                        (tableName === 'Overall Conclusion' &&
                          (key === 'Test Procedures' ||
                            key === 'Control Activity')) ||
                        (tableName === 'Operating Table' &&
                          (key === 'Test Procedures' || key === 'Control No.'))
                          ? false
                          : true
                      }
                      options={RCMData.meta.fields[key]?.options}
                      styles={{
                        padding: '10px',
                        margin: '5px',
                        borderRadius: '5px'
                      }}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </Table>
        </TableContainer>
      </Box>
    ));
  };

  const renderWorkPaperReference = () => {
    if (testingSheetType === 'BDO') return null;

    return (
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '10px'
        }}
      >
        <Typography sx={{ fontWeight: 'bold' }} variant="h6">
          Work Paper Reference
        </Typography>
        <GetTableCell
          key={`rcm-link-proof`}
          rowNumber={rowIndex}
          fieldName="Work Paper Reference"
          fieldType={RCMData.meta.fields['Proofs Link']?.fieldType}
          value={RCMData['rows']['Master Table'][rowIndex]['Proofs Link']}
          onChangeCB={(rowNumber, fieldName, cellValue) =>
            handleChangeRCM(rowNumber, 'Proofs Link', cellValue, 'Master Table')
          }
          isEditable={true}
          styles={{ marginLeft: '10px' }}
        />
      </Box>
    );
  };

  return (
    <>
      {type === 'BDO_SAP' && testingType === 'Termination' && (
        <Box sx={{ mb: 3 }}>
          <TerminationCheckForm
            onSubmit={handleTerminationCheckSubmit}
            isLoading={isTerminationCheckLoading}
          />
        </Box>
      )}
      {type === 'BDO_SAP' && testingType === 'Generic' && (
        <Box sx={{ mb: 3 }}>
          <GenericCheckForm
            onSubmit={handleGenericCheckSubmit}
            isLoading={isGenericCheckLoading}
          />
        </Box>
      )}
      {type === 'BDO_SAP' && testingType === 'Privilege' && (
        <Box sx={{ mb: 3 }}>
          <PrivilegeCheckForm
            onSubmit={handlePrivilegeCheckSubmit}
            isLoading={isPrivilegeCheckLoading}
          />
        </Box>
      )}
      {type === 'BDO_SAP' && testingType === 'SensitiveTCodes' && (
        <Box sx={{ mb: 3 }}>
          <SensitiveTcodeCheckForm
            onSubmit={handleSensitiveTcodeCheckSubmit}
            isLoading={isSensitiveTcodeCheckLoading}
          />
        </Box>
      )}
      {type === 'BDO_SAP' && testingType === 'Password' && (
        <Box sx={{ mb: 3 }}>
          <PasswordCheckForm
            onSubmit={handlePasswordCheckSubmit}
            isLoading={isPasswordCheckLoading}
          />
        </Box>
      )}
      {type === 'BDO_SAP' && testingType === 'Backup' && (
        <Box sx={{ mb: 3 }}>
          <BackupCheckForm
            onSubmit={handleBackupCheckSubmit}
            isLoading={isBackupCheckLoading}
          />
        </Box>
      )}
      {renderer === 'dynamic' && renderDynamicSheet()}
      {renderer === 'audit' && renderAuditSheet()}
      {renderer === 'testing' && renderTestingSheet()}
      {renderer === 'soc2' && renderSOC2Sheet()}
      {renderWorkPaperReference()}
    </>
  );
};

export default MasterTableSheet;
